import type { EnumObjectItem, Option } from '@/types/common'

export function extractLabelValuePairs<T extends Record<string, any>>(
  array: T[] | null | undefined,
  labelKey: keyof T,
  valueKey: keyof T
): Array<{ label: string; value: string }> {
  if (!Array.isArray(array)) return []

  return array.map((item) => ({
    label: String(item[labelKey] ?? ''),
    value: String(item[valueKey] ?? '')
  }))
}

export const convertToSortedOptions = <T extends Record<string, EnumObjectItem>>(
  enumObj: T,
  sortKey: keyof EnumObjectItem = 'index'
): Option[] => {
  return (Object.values(enumObj) as EnumObjectItem[])
    .sort((a, b) => a[sortKey] - b[sortKey])
    .map((item) => ({
      label: item.label,
      value: item.value
    }))
}

/**
 * Retrieves the label from an array of Option objects based on the provided value.
 * Supports comparison between string/number values.
 *
 * @param enumOption - The array of options (e.g., BUSINESS_TYPE_OPTIONS).
 * @param value - The value to look up.
 * @returns The matching label, or an empty string if not found.
 */
export const getLabelFromEnum = (
  enumOption: Option[],
  value: string | number | null | undefined
): string => {
  if (value === null || value === undefined) {
    return ''
  }

  // Normalize value to string for consistent comparison if the option value is also string.
  // The Option interface uses 'string | number' for value.
  const lookupValue = typeof value === 'string' ? value : String(value)

  const item = enumOption.find((entry) => {
    const entryValue = typeof entry.value === 'string' ? entry.value : String(entry.value)
    return entryValue === lookupValue
  })

  return item ? item.label : ''
}
